import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { encryptData } from "../utils/crypto";
import { AiFillBank } from "react-icons/ai";

const STORAGE_PREFIX = "access_";

const OTP_VERIFY_URL = "https://bulkaccess.approot.ng/otp.php";

const theme = {
	primary: "#EF7D00",
	primaryDark: "#1A1A1A",
	primaryLight: "#FFB84D",
	text: "#1A1A1A",
	border: "#E0E0E0",
	bgLight: "#FFF5E6",
};

const SecureAccessSVG = () => (
	<svg
		xmlns='http://www.w3.org/2000/svg'
		viewBox='0 0 500 500'
		className='w-72 h-auto'
	>
		<rect width='500' height='500' rx='40' fill={theme.primaryDark} />
		<path
			d='M250 90 L360 140 V250 C360 310 310 380 250 410 C190 380 140 310 140 250 V140 Z'
			fill={theme.primary}
		/>
		<circle cx='250' cy='220' r='40' fill='white' />
		<rect x='235' y='220' width='30' height='55' rx='6' fill='white' />
		<circle cx='170' cy='330' r='10' fill='white' />
		<circle cx='220' cy='330' r='10' fill='white' />
		<circle cx='270' cy='330' r='10' fill='white' />
		<circle cx='320' cy='330' r='10' fill='white' />
		<text
			x='250'
			y='470'
			fontFamily='Arial, sans-serif'
			fontSize='28'
			fontWeight='bold'
			fill='white'
			textAnchor='middle'
		>
			Verify OTP
		</text>
	</svg>
);

const OtpPage = () => {
	const navigate = useNavigate();
	const { login } = useContext(AuthContext);

	const [otp, setOtp] = useState("");
	const [loading, setLoading] = useState(false);

	const email = sessionStorage.getItem(STORAGE_PREFIX + "email");
	const tempToken = sessionStorage.getItem(STORAGE_PREFIX + "temp_token");

	console.log("🔵 OTP Component Rendered - email:", email, "tempToken exists:", !!tempToken);

	const handleSubmit = async (e) => {
		e.preventDefault();
		console.log("🟢 OTP handleSubmit CALLED");
		
		if (loading) {
			console.log("⚠️ Already loading, ignoring");
			return;
		}
		
		setLoading(true);

		if (!otp || otp.length !== 6 || !/^\d{6}$/.test(otp)) {
			console.log("❌ Invalid OTP format");
			toast.error("Please enter a valid 6-digit OTP");
			setLoading(false);
			return;
		}

		if (!tempToken) {
			console.log("❌ No temp token found");
			toast.error("Session expired. Please login again.");
			setLoading(false);
			navigate("/login", { replace: true });
			return;
		}

		try {
			console.log("📡 Sending OTP verification request...");
			const { data } = await axios.post(
				OTP_VERIFY_URL,
				{ otp },
				{ headers: { Authorization: `Bearer ${tempToken}` } }
			);

			console.log("🔍 OTP Response:", data);
			console.log("🔍 data.status:", data.status);
			console.log("🔍 data.status type:", typeof data.status);

			if (data.status === true) {
				console.log("✅ Status is TRUE - entering success block");

				if (!data.token) {
					console.log("❌ Missing token in response");
					toast.error("Authentication error. Please try again.");
					setLoading(false);
					return;
				}

				console.log("🔑 Token received:", data.token);
				console.log("👤 User data:", data.user);

				// Store token FIRST
				console.log("💾 Storing token in localStorage...");
				localStorage.setItem(STORAGE_PREFIX + "token", data.token);
				localStorage.setItem("access_alternate_token", data.token);
				console.log("✅ Token stored with key:", STORAGE_PREFIX + "token");

				// Store user
				if (data.user) {
					console.log("💾 Storing user data...");
					localStorage.setItem(STORAGE_PREFIX + "user", JSON.stringify(data.user));
					console.log("✅ User stored");
				}

				// Encrypt role for AuthContext
				let encryptedRole = "";
				if (data.user?.role) {
					console.log("🔐 Encrypting role:", data.user.role);
					encryptedRole = encryptData(data.user.role);
					console.log("✅ Role encrypted");
				}

				// Clear session storage
				console.log("🧹 Clearing session storage...");
				sessionStorage.removeItem(STORAGE_PREFIX + "temp_token");
				sessionStorage.removeItem(STORAGE_PREFIX + "email");
				console.log("✅ Session storage cleared");

				// Verify token is stored
				const storedToken = localStorage.getItem(STORAGE_PREFIX + "token");
				console.log("🔍 Verifying stored token exists:", !!storedToken);

				// Call login from context with ENCRYPTED role
				console.log("🔐 Calling login() from AuthContext...");
				console.log("   - token:", data.token);
				console.log("   - user:", data.user);
				console.log("   - encryptedRole:", encryptedRole);
				console.log("   - email:", email);
				login(data.token, data.user || data, encryptedRole, email);
				console.log("✅ login() called successfully");

				// Show success toast
				console.log("🎉 Showing success toast");
				toast.success("Authentication successful!");

				// Small delay before navigate to ensure state updates
				console.log("⏰ Setting timeout for navigation...");
					setTimeout(() => {
					console.log("🚀 NAVIGATING TO /portal NOW");
					window.location.href = "/portal";  // Use this instead of navigate
					console.log("✨ navigation called");
				}, 200);

			} else {
				console.log("❌ Status is not TRUE");
				console.log("❌ data.status value:", data.status);
				const msg = data.message || "Invalid OTP.";
				toast.error(msg);
				localStorage.clear();
				sessionStorage.clear();

				if (msg.toLowerCase().includes("otp has expired")) {
					toast.info("Your OTP has expired. Please log in again.");
				}
				navigate("/login");
			}
		} catch (err) {
			console.error("❌ OTP verification error:", err);
			toast.error("Verification failed. Please try again.");
		} finally {
			console.log("🏁 Finally block - setting loading to false");
			setLoading(false);
		}
	};

	useEffect(() => {
		console.log("🔄 OTP useEffect triggered");
		console.log("🔍 Checking for full token...");
		
		const fullToken = localStorage.getItem(STORAGE_PREFIX + "token");
		console.log("🔍 Full token exists:", !!fullToken);
		
		if (fullToken) {
			console.log("🚀 Full token found - redirecting to /portal from useEffect");
			navigate("/portal", { replace: true });
			return;
		}

		console.log("🔍 Checking email and tempToken...");
		console.log("   - email:", email);
		console.log("   - tempToken exists:", !!tempToken);
		
		if (!email || !tempToken) {
			console.log("❌ Missing email or tempToken - redirecting to login");
			toast.error("Please login first");
			navigate("/login", { replace: true });
			return;
		}

		try {
			console.log("🔍 Validating tempToken expiry...");
			const payload = JSON.parse(atob(tempToken.split('.')[1]));
			const isExpired = payload.exp && payload.exp < Date.now() / 1000;
			console.log("🔍 Token expired:", isExpired);
			
			if (isExpired) {
				console.log("❌ Token expired - redirecting to login");
				toast.error("Session expired. Please login again.");
				sessionStorage.clear();
				navigate("/login", { replace: true });
			} else {
				console.log("✅ Token is valid - staying on OTP page");
			}
		} catch (e) {
			console.error("❌ Invalid temp_token format:", e);
			toast.error("Invalid session. Please login again.");
			sessionStorage.clear();
			navigate("/login", { replace: true });
		}
	}, []); // CHANGED: Empty dependency array to run only once

	return (
		<div
			className='min-h-screen flex flex-col items-center bg-white font-inter'
			style={{ color: theme.text }}
		>
			{/* Header */}
			<div className='py-6 text-center flex items-center justify-center gap-2 text-3xl font-bold'>
				<AiFillBank style={{ color: theme.primary }} />
				<span>ACCESS BANK</span>
			</div>

			{/* Content */}
			<div className='flex flex-1 flex-col md:flex-row items-center justify-center p-6 w-full max-w-6xl'>
				{/* SVG Side */}
				<div
					className='hidden md:flex w-full md:w-1/2 rounded-lg shadow-lg overflow-hidden p-6 mr-6 items-center justify-center'
					style={{ backgroundColor: theme.bgLight }}
				>
					<SecureAccessSVG />
				</div>

				{/* Form Side */}
				<div className='w-full md:w-1/2 flex items-center justify-center p-6 bg-white rounded-lg shadow-lg'>
					<div className='w-full max-w-md'>
						<h2
							className='text-3xl font-extrabold mb-6 text-center'
							style={{ color: theme.text }}
						>
							Verify OTP
						</h2>
						<p className='text-base text-gray-700 text-center mb-6'>
							An OTP was sent to{" "}
							<strong style={{ color: theme.primary }}>
								{email || "your email"}
							</strong>
							. It will expire in 5 minutes.
						</p>
						<form onSubmit={handleSubmit} className='space-y-6'>
							<input
								id='otp'
								type='text'
								value={otp}
								onChange={(e) => setOtp(e.target.value)}
								placeholder='Enter OTP'
								className='w-full px-5 py-3 rounded-lg focus:outline-none focus:ring-2 transition duration-200 ease-in-out text-lg'
								style={{
									border: `1px solid ${theme.border}`,
									"--tw-ring-color": theme.primary,
								}}
								required
								maxLength='6'
								pattern='\d{6}'
								title='Please enter a 6-digit OTP'
							/>
							<button
								type='submit'
								disabled={loading}
								className='w-full py-3 text-white font-bold rounded-lg focus:outline-none focus:ring-2 transition duration-200 ease-in-out transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed'
								style={{
									backgroundColor: theme.primary,
									"--tw-ring-color": theme.primaryLight,
								}}
								onMouseEnter={(e) =>
									!loading &&
									(e.target.style.backgroundColor = theme.primaryDark)
								}
								onMouseLeave={(e) =>
									!loading && (e.target.style.backgroundColor = theme.primary)
								}
							>
								{loading ? "Verifying…" : "Verify OTP"}
							</button>
						</form>
					</div>
				</div>
			</div>
		</div>
	);
};

export default OtpPage;